import * as Hammer from "hammerjs";
import * as Logger from "js-logger";
import { Signal } from "signals";
import { AssetConfigSchema } from "slotworks/config/asset-config-schema";
import GameLoop from "slotworks/core/game-loop";
import Layers from "slotworks/graphics/layers/layers";
import { AddWindowFocusChangeListener } from "slotworks/utils/browser-utils";
import { nearestUnder } from "slotworks/utils/collection-utils";
import DeviceInfo from "slotworks/utils/device-info";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import Timer from "slotworks/utils/timer";

declare const __DEBUG__: boolean;

export enum Orientation {
    LANDSCAPE = "landscape",
    PORTRAIT = "portrait"
}

export enum ResolutionName {
    LD = "ld",
    MD = "md",
    HD = "hd"
}

class CanvasManager {

    public FULLSCREEN_IN_IFRAME = true;

    public stage: PIXI.Container;

    /**
     * Theoretical width of the stage (doesn't include pixel density or css scaling)
     */
    public stageWidth: number;
    /**
     * Theoretical height of the stage (doesn't include pixel density or css scaling)
     */
    public stageHeight: number;

    public landscapeViewport: Rectangle;
    public portraitViewport: Rectangle;

    /**
     * Pixel density pixi will render at. Shouldn't really need to access this as pixi will handle everything automatically
     */
    public resolution: number;
    public layoutScale: number;
    public resolutionName: ResolutionName;

    public renderer: PIXI.WebGLRenderer | PIXI.CanvasRenderer;
    public canvas: HTMLCanvasElement;

    /**
     * Enabled scroll prompt overlay for iOS full screen
     */
    public iosScrollPromptEnabled: boolean = true;

    /**
     * Set valid orientations for overlay.
     */
    public validOrientations: Orientation[] = [];

    /**
     * For gestures
     */
    public hammer: HammerManager;

    public headerDOMContainer: HTMLDivElement;
    public footerDOMContainer: HTMLDivElement;
    public portraitDOMContainer: HTMLDivElement;
    public landscapeDOMContainer: HTMLDivElement;

    public orientation: Orientation = Orientation.LANDSCAPE;
    public onOrientationChange: Signal = new Signal();

    public rootNode: HTMLElement = document.body;
    private fixedPositionBackground = false;
    private portraitCanvasEnabled: boolean = false;
    private portraitEnabled: boolean = true;
    private landscapeScale: number = 1;
    private portraitScale: number = 1;
    private landScapeOffset: Point = new Point(0, 0);
    private portraitOffset: Point = new Point(0, 0);
    private landscapeAnchor: Point = new Point(0.5, 0.5);
    private portraitAnchor: Point = new Point(0.5, 0.5);
    private landscapeBackgroundOffset: Point;
    private portraitBackgroundOffset: Point;

    private isGameLoaded: boolean;
    private iosEnableZoom: boolean = false;
    private previousScreenWidth: number;
    private previousScreenHeight: number;

    private fullScreenPaused = false;
    private resumeFullScreen = false;

    /**
     * Creates a pixi renderer (WebGL with canvas fallback), an HTML canvas element and a pixi stage container
     * Uses graphics config to intialize factory methods
     * Starts render loop (requestAnimationFrame)
     */
    public init(assetConfig: AssetConfigSchema) {

        // Show the document body
        if (DeviceInfo.isIE11()) {
            this.rootNode.style.display = "block";
        } else {
            this.rootNode.style.display = "initial";
        }

        // Delete root elements which aren't this layout
        for (let i = this.rootNode.childNodes.length - 1; i >= 0; i--) {
            const element = this.rootNode.childNodes.item(i) as HTMLElement;
            if (element.id !== DeviceInfo.layoutId && element.id !== "all") {
                this.rootNode.removeChild(element);
            }
        }

        this.headerDOMContainer = document.getElementById("headerDOMContainer") as HTMLDivElement;
        this.footerDOMContainer = document.getElementById("footerDOMContainer") as HTMLDivElement;
        this.portraitDOMContainer = document.getElementById("portrait") as HTMLDivElement;
        this.landscapeDOMContainer = document.getElementById("landscape") as HTMLDivElement;

        const layoutConfig = assetConfig.layouts[DeviceInfo.layoutId];
        const layoutSettings = layoutConfig.settings;
        const layoutScales = layoutSettings.scales;
        const nativePixelDensity = layoutSettings.nativePixelRatio;

        // Some devices may only have a 1x pixel density, but still possess enough pixels
        // to render higher scale graphics for this layout
        const width1x = layoutSettings.landscapeWidth / nativePixelDensity;
        const deviceWidth = Math.max(screen.width * DeviceInfo.pixelDensity, screen.height * DeviceInfo.pixelDensity);
        const psuedoPixelRatio = Math.round(deviceWidth / width1x * 100) / 100;

        this.layoutScale = nearestUnder(Math.round(psuedoPixelRatio / nativePixelDensity * 100) / 100, layoutScales);

        // Screens lower than the smallest resolution should use the smallest
        if (this.layoutScale === -1) {
            this.layoutScale = layoutScales[0];
        }

        this.resolution = Math.round(this.layoutScale * nativePixelDensity * 100) / 100;
        this.resolutionName = [ResolutionName.LD, ResolutionName.MD, ResolutionName.HD][layoutScales.indexOf(this.layoutScale)];

        // Always use lowest res assets on iPhone 4
        if (DeviceInfo.isIphone4()) {
            this.resolution = Math.round(layoutScales[0] * nativePixelDensity * 100) / 100;
            this.resolutionName = ResolutionName.LD;
        }

        // Always use highest res assets on desktop
        if (DeviceInfo.isDesktop) {
            this.resolution = Math.round(layoutScales[layoutScales.length - 1] * nativePixelDensity * 100) / 100;
            this.resolutionName = ResolutionName.HD;
        }

        this.log("Resolution: " + this.resolutionName + ": @" + this.resolution + "x" + "\n" +
            "scale: " + this.layoutScale + "\n" +
            "natPixRatio: " + nativePixelDensity + "\n" +
            "nativeWidth: " + layoutSettings.landscapeWidth + "\n" +
            "nativeHeight: " + layoutSettings.landscapeHeight + "\n" +
            "DeviceInfo.pixelDensity: " + DeviceInfo.pixelDensity + "\n" +
            "screen.height: " + screen.height + "\n" +
            "screen.width: " + screen.width + "\n" +
            "psuedoPixelRatio: " + psuedoPixelRatio
        );

        this.log("Resolution / atlas scale is @" + this.resolution + "x");

/////////////////////////
////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////
/////////
//////////////////

        // Give entire document class to determine pixel density (used for CSS psuedo queries)
        this.rootNode.classList.toggle(this.resolutionName, true);

        // Give entire document class to weed out iOS bugs
        if (DeviceInfo.isAppleDevice()) {
            this.rootNode.classList.toggle("iOS", true);
        }

        // Give entire document class to tell it it's an iPhone 5,
        // and requires special treatment (to compensate for 44px dead zone at footer)
        if (DeviceInfo.isIphone5()) {
            this.rootNode.classList.toggle("iPhone5", true);
        }

        if (DeviceInfo.hasTaskBar()) {
            this.rootNode.classList.toggle("iPhoneTaskBar", true);
        }

        if (DeviceInfo.isIpad()) {
            this.rootNode.classList.toggle("iPad", true);
        }

        this.landscapeViewport = new Rectangle(0, 0, layoutSettings.landscapeWidth, layoutSettings.landscapeHeight);
        this.portraitViewport = new Rectangle(layoutSettings.portraitOffsetX, layoutSettings.portraitOffsetY, layoutSettings.portraitWidth, layoutSettings.portraitHeight);

        this.stageWidth = this.landscapeViewport.width;
        this.stageHeight = this.landscapeViewport.height;

        // test if highp is supported (Pixi V5 does this automatically, so if we upgrade, this can be removed)
        // Some Android devices (OnePlus, Pixel 3) have rendering glitches (random artifacts all over the screen) with this,
        // so only enabling on Apple devices, which suffer the most from the animation wobble created by mediump
        if (DeviceInfo.isAppleDevice()) {
            const gl = PIXI.glCore.createContext(window.document.createElement("canvas"));
            if (gl.getShaderPrecisionFormat) {
                const fragmentShader = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
                PIXI.settings.PRECISION_FRAGMENT = fragmentShader.precision !== 0 ? "highp" : "mediump";
            }
        }

        const rendererOptions = {
            transparent: true,
            resolution: this.resolution / nativePixelDensity,
            roundPixels: false,
            antialias: false
        };

        if (DeviceInfo.isIE11()) {
            this.renderer = new PIXI.WebGLRenderer(this.stageWidth, this.stageHeight, rendererOptions);
        } else {
            this.renderer = PIXI.autoDetectRenderer(this.stageWidth, this.stageHeight, rendererOptions);
        }

        this.canvas = this.renderer.view;
        this.canvas.id = "canvas";
        this.rootNode.appendChild(this.canvas);

        this.stage = new PIXI.Container();

        if (DeviceInfo.isIE11()) {
            window.onresize = () => this.resize();
        }
        window.addEventListener("resize", (event) => this.resize(event));
        window.addEventListener("load", (event) => this.resize(event));
        window.addEventListener("orientationchange", (event) => this.resize(event));
        Timer.setInterval(() => this.resizePoll(), 0);
        AddWindowFocusChangeListener(() => this.resize());

        this.resize();

        const resolutionString = this.stageWidth + "x" + this.stageHeight + "@" + this.resolution + "x (" + this.renderer.width + "x" + this.renderer.height + "), ";
        this.log("Canvas created at " + resolutionString);

        // Gesture listeners
        this.hammer = new Hammer.Manager(this.canvas);

        if (this.fullscreenEnabled() && !DeviceInfo.isAppleDevice()) {
            this.canvas.addEventListener("touchend", () => this.requestFullScreen());

            document.onclick = () => this.requestFullScreen();
        }

        // Apple hacks / fixes
        if (DeviceInfo.isAppleDevice()) {
            // iOS hack to force honoring of user-scale
            document.addEventListener("touchmove", (moveEvent: any) => {
                if (!this.iosEnableZoom && moveEvent.scale !== 1) {
                    moveEvent.preventDefault();
                }
            }, false);
            // iOS hack to prevent double tap zooming
            let lastTouchEnd = 0;
            document.addEventListener("touchend", (touchEvent: any) => {
                const now = (new Date()).getTime();
                if (now - lastTouchEnd <= 500) {
                    touchEvent.preventDefault();
                }
                lastTouchEnd = now;
            }, false);
        }
    }

    public setLandscapeScale(scale: number, anchor?: Point, offset?: Point) {
        this.landscapeScale = scale;
        if (anchor) {
            this.landscapeAnchor = anchor;
        }
        if (offset) {
            this.landScapeOffset = offset;
        }
        this.resize();
    }

    // Sets whether or not canvas should change size and use portrait positions if screen height > width
    public setPortraitCanvasEnabled(enabled: boolean) {
        this.portraitCanvasEnabled = enabled;
    }

    // Sets whether or not to use the DOM portrait container & root css class
    public setPortraitEnabled(enabled: boolean) {
        this.portraitEnabled = enabled;
    }

    public setPortraitScale(scale: number, anchor?: Point, offset?: Point) {
        this.portraitScale = scale;
        if (anchor) {
            this.portraitAnchor = anchor;
        }
        if (offset) {
            this.portraitOffset = offset;
        }
        this.resize();
    }

    public gameLoaded() {
        this.isGameLoaded = true;
        this.resize();
    }

    public useFixedPositionBackground(landscapeOffset: Point, portraitOffset: Point) {
        this.landscapeBackgroundOffset = landscapeOffset;
        this.portraitBackgroundOffset = portraitOffset;
        this.fixedPositionBackground = true;

        Layers.Background.onSceneEnter.add((scene: string) => {
            this.resize();
        });
    }

    public setFullScreenPaused(pause: boolean) {
        this.fullScreenPaused = pause;

        if (this.isFullScreen() && pause) {
            this.resumeFullScreen = true;
            this.exitFullScreen();
        }

        if (this.resumeFullScreen && !pause) {
            this.resumeFullScreen = false;
            this.requestFullScreen();
        }
    }

    private resizePoll() {
        if (DeviceInfo.getScreenWidth() === this.previousScreenWidth && DeviceInfo.getScreenHeight() === this.previousScreenHeight) {
            return;
        }
        this.previousScreenWidth = DeviceInfo.getScreenWidth();
        this.previousScreenHeight = DeviceInfo.getScreenHeight();

        this.resize();
    }

    private resize(event?: any) {
        let canvasDOMContainer: HTMLElement;
        let zoomLevel: number;
        let anchor: Point;
        let backgroundOffset: Point;

        const previousOrientation = this.orientation;

        // Set orientation
        if (DeviceInfo.getScreenWidth() > DeviceInfo.getScreenHeight() || !this.portraitEnabled) {
            this.rootNode.classList.toggle("portrait", false);
            this.rootNode.classList.toggle("landscape", true);

            if (this.portraitDOMContainer) {
                this.portraitDOMContainer.style.display = "none";
            }
            if (this.landscapeDOMContainer) {
                this.landscapeDOMContainer.style.display = "block";
            }

            canvasDOMContainer = document.querySelector("#landscape #canvasDOMContainer") as HTMLElement;
            zoomLevel = this.landscapeScale;
            anchor = this.landscapeAnchor;
            backgroundOffset = this.landscapeBackgroundOffset;
            this.orientation = Orientation.LANDSCAPE;
        } else {
            this.rootNode.classList.toggle("portrait", true);
            this.rootNode.classList.toggle("landscape", false);

            if (this.portraitDOMContainer) {
                this.portraitDOMContainer.style.display = "block";
            }
            if (this.landscapeDOMContainer) {
                this.landscapeDOMContainer.style.display = "none";
            }

            canvasDOMContainer = document.querySelector("#portrait #canvasDOMContainer") as HTMLElement;
            zoomLevel = this.portraitScale;
            anchor = this.portraitAnchor;
            backgroundOffset = this.portraitBackgroundOffset;
            this.orientation = Orientation.PORTRAIT;
        }

        if (!canvasDOMContainer) {
            canvasDOMContainer = document.querySelector("#canvasDOMContainer") as HTMLElement;
        }

        if (this.portraitCanvasEnabled) {
            let viewport = this.landscapeViewport;

            if (this.orientation === Orientation.PORTRAIT) {
                viewport = this.portraitViewport;
            }

            this.stage.x = viewport.x;
            this.stage.y = viewport.y;
            this.stageWidth = viewport.width;
            this.stageHeight = viewport.height;

            this.renderer.resize(this.stageWidth, this.stageHeight);

            if (this.orientation !== previousOrientation) {
                this.onOrientationChange.dispatch(this.orientation);
            }
        } else {
            this.orientation = Orientation.LANDSCAPE;
        }

        const offset = this.orientation === Orientation.PORTRAIT ? this.portraitOffset : this.landScapeOffset;

        // Resize canvas including zoom
        this.resizeElement(this.canvas, zoomLevel, anchor, offset);

        if (canvasDOMContainer) {
            this.resizeElement(canvasDOMContainer, zoomLevel, anchor, offset, true);
        }

        if (this.fixedPositionBackground) {
            const backgrounds = Layers.Background.getDOMContainers();
            backgrounds.forEach((background: HTMLElement) => {
                background.style.backgroundRepeat = "no-repeat";
                background.style.backgroundPosition = "center center";
                background.style.transformOrigin = "left top";

                backgroundOffset = new Point(backgroundOffset.x + offset.x, backgroundOffset.y + offset.x);
                this.resizeBackground(background, anchor, backgroundOffset);
            });
        }

        // Header DOM container
        if (canvasDOMContainer && this.headerDOMContainer) {
            this.headerDOMContainer.style.width = DeviceInfo.getScreenWidth() + "px";
            this.headerDOMContainer.style.height = parseFloat(canvasDOMContainer.style.marginTop) + "px";
            canvasDOMContainer.style.marginTop = this.headerDOMContainer.style.height;
            if (this.footerDOMContainer) {
                this.footerDOMContainer.style.marginTop = parseFloat(canvasDOMContainer.style.height) + "px";
            }
        }

        // Footer DOM container
        if (canvasDOMContainer && this.footerDOMContainer) {
            this.footerDOMContainer.style.width = DeviceInfo.getScreenWidth() + "px";
            this.footerDOMContainer.style.height =
                (DeviceInfo.getScreenHeight() - parseFloat(canvasDOMContainer.style.height) - parseFloat(canvasDOMContainer.style.marginTop)) + "px";
            this.footerDOMContainer.style.marginTop = this.roundDownForDensity(DeviceInfo.getScreenHeight() - parseFloat(this.footerDOMContainer.style.height)) + "px";
        }

        this.scrollPrompt();
        this.rotationPrompt();
    }

    /**
     * Resizes an element to fit on screen
     *
     * @param element The element to resize
     * @param zoomLevel A custom zoom level to take into account, for manually overflowing the game; i.e. for portrait if the edges need to be cut off
     * @param anchor Where should the game centered when resized (top, middle, bottom etc)
     * @param limitToScreen Once scaled, if there is overflow, should it be cropped or included in the element width and height.
     *                      e.g, canvas and background should, full screen elements such as UI (canvasDOMContainer etc) should not
     * @param maintainAspect
     */
    private resizeElement(element: HTMLElement, zoomLevel: number = 1, anchor: Point, offset: Point, limitToScreen: boolean = false, maintainAspect: boolean = true) {
        const elementWidth = this.stageWidth;
        const elementHeight = this.stageHeight;

        const viewPortWidth = DeviceInfo.getScreenWidth() * zoomLevel;
        const viewPortHeight = DeviceInfo.getScreenHeight() * zoomLevel;

        let scaleX = viewPortWidth / elementWidth;
        let scaleY = viewPortHeight / elementHeight;

        if (maintainAspect) {
            const scale = Math.min(scaleX, scaleY);
            scaleX = scale;
            scaleY = scale;
        }

        let scaledWidth = this.roundUpForDensity(elementWidth * scaleX);
        let scaledHeight = this.roundUpForDensity(elementHeight * scaleY);

        if (limitToScreen) {
            scaledWidth = Math.min(scaledWidth, DeviceInfo.getScreenWidth());
            scaledHeight = Math.min(scaledHeight, DeviceInfo.getScreenHeight());
        }

        element.style.width = scaledWidth + "px";
        element.style.height = scaledHeight + "px";

        offset = new Point(offset.x * scaledWidth, offset.y * scaledHeight);

        const marginTop = this.roundDownForDensity((DeviceInfo.getScreenHeight() - scaledHeight) * anchor.y) + offset.y;
        const marginRight = this.roundDownForDensity((DeviceInfo.getScreenWidth() - scaledWidth) * (1 - anchor.x) - offset.x);
        const marginBottom = this.roundDownForDensity((DeviceInfo.getScreenHeight() - scaledHeight) * (1 - anchor.y) - offset.y);
        const marginLeft = this.roundDownForDensity((DeviceInfo.getScreenWidth() - scaledWidth) * anchor.x) + offset.x;
        const padding = "0";
        const margin = marginTop + "px " + marginRight + "px " + marginBottom + "px " + marginLeft + "px";
        const fontScale = Math.min(scaledWidth / this.landscapeViewport.width, scaledHeight / this.landscapeViewport.height) * 100;

        element.style.padding = padding;
        element.style.margin = margin;
        element.style.display = "block";
        element.style.fontSize = fontScale.toString() + "px";
    }

    private resizeBackground(background: HTMLElement, anchor: Point, offset: Point): void {
        const viewportWidth = DeviceInfo.getScreenWidth();
        const viewportHeight = DeviceInfo.getScreenHeight();

        const scale = Math.min(
            viewportWidth / this.stageWidth,
            viewportHeight / this.stageHeight
        );
        background.style.transform = `scale(${scale}, ${scale})`;

        const scaledBounds = background.getBoundingClientRect();
        const top = this.roundDownForDensity((viewportHeight - scaledBounds.height) * anchor.y + offset.y * scale);
        const left = this.roundDownForDensity((viewportWidth - scaledBounds.width) * anchor.x + offset.x * scale);
        background.style.marginTop = `${top}px`;
        background.style.marginLeft = `${left}px`;
    }

    private roundUpForDensity(n: number) {
        return Math.ceil(n / this.resolution) * this.resolution;
    }

    private roundDownForDensity(n: number) {
        return Math.floor(n / this.resolution) * this.resolution;
    }

    private requestFullScreen() {
        if (!this.fullScreenPaused && !DeviceInfo.isDesktop) {
            let element: any;
            try {
                element = window.top.document.body;
            } catch (e) {
                element = window.document.body;
            }

            // Supports most browsers and their versions.
            const requestMethod = element.requestFullScreen || element.webkitRequestFullScreen || element.mozRequestFullScreen || element.msRequestFullScreen;

            if (requestMethod) {
                // Native full screen.
                requestMethod.call(element);
            }
        }
    }

    private exitFullScreen() {
        if (!DeviceInfo.isDesktop) {
            if (this.isFullScreen()) {

                const doc: any = window.document;
                let promise;

                if (doc.exitFullscreen) {
                    promise = doc.exitFullscreen();
                } else if (doc.msExitFullscreen) {
                    promise = doc.msExitFullscreen();
                } else if (doc.mozCancelFullScreen) {
                    promise = doc.mozCancelFullScreen();
                } else if (doc.webkitExitFullscreen) {
                    promise = doc.webkitExitFullscreen();
                }

                if (promise && promise.catch) {
                    promise.catch(() => 0);
                }
            }
        }
    }

    private scrollPrompt() {
        if (DeviceInfo.isAppleDevice() && this.isGameLoaded) {

            const iOSChromeVisible = DeviceInfo.iOSChromeVisible();

            this.rootNode.classList.toggle("iosChromeVisible", iOSChromeVisible);

            if (this.iosScrollPromptEnabled) {

                if (window && window.scrollTo) {
                    window.scrollTo(0, 0);
                }

                const scrollPromptDiv = document.getElementById("iosScroll");

                if (!iOSChromeVisible) {
                    if (scrollPromptDiv) {
                        scrollPromptDiv.classList.toggle("visible", false);
                        scrollPromptDiv.classList.toggle("hidden", true);
                    }
                    this.rootNode.classList.toggle("noOverflow", true);
                } else {
                    if (scrollPromptDiv) {
                        scrollPromptDiv.classList.toggle("visible", true);
                        scrollPromptDiv.classList.toggle("hidden", false);
                    }
                    this.rootNode.classList.toggle("noOverflow", false);
                }
            }
        }
    }

    private rotationPrompt() {
        if (this.isGameLoaded) {
            if (this.validOrientations.length > 0) {
                const valid: boolean = this.validOrientations.indexOf(this.orientation) !== -1;
                GameLoop.setPaused(!valid);
            }
        }
    }

    private isFullScreen() {
        const doc: any = document;

        return doc.fullscreenElement !== null || doc.mozFullScreenElement !== null || doc.webkitFullscreenElement !== null || doc.msFullscreenElement !== null;
    }

    private isInIFrame() {
        return top !== self;
    }

    private fullscreenEnabled() {
        if (this.isInIFrame()) {
            return this.FULLSCREEN_IN_IFRAME;
        }
        return !DeviceInfo.isSamsungBrowser();
    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string) {
        Logger.info("%c Canvas Manager ", "background: #f96; color: #fff", message);
    }
}

export default new CanvasManager();
